import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PortalScope } from '../../login/login.component';
import { Constants } from '../../services/constants';
import { PortalServiceFactory } from '../../services/portal-service.factory';
import { BasePortalService } from '../../services/base-portal.service';
import { Observer, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Alert } from 'projects/shared/models/alert.model';
import { AlertType } from 'projects/shared/models/alert-type.enum';
import { EsignOrder, ValidateESignPropertyInfoResponse } from '../../models/e-sign/validate-esign-property-info.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CreateSignerViewResponse } from '../../models/e-sign/create-signer-view-response.model';
import { OpenSignerViewRequest } from '../../models/open-signer-view-request.model';
import { NgForm } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import * as _ from 'lodash';
import { PortalContent } from '../../models/borrower-portal/portal-content-model-model';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { ESignCompletionContext } from '../../models/e-sign/e-sign-completion-context.model';

@Component({
    selector: 'e-sign-via-token',
    templateUrl: 'e-sign-via-token.component.html',
    styleUrls: ['./e-sign-via-token.component.scss'],
    standalone: false
})
export class ESignViaTokenComponent implements OnInit, OnDestroy {

  @ViewChild('loanValidationForm')
  loanValidationForm: NgForm;

  // The signing iframe currently rendered - only one vendor's iframe exists at a time. A token request is
  // answered only when it came from that window, so the request cannot be forged by another frame or opener.
  @ViewChild('signingIframe')
  private _signingIframe?: ElementRef<HTMLIFrameElement>;

  protected userScope: PortalScope = PortalScope.Borrower;

  protected alert: Alert;
  protected loadingMessage: string;

  protected isLoading: boolean = false;
  protected readyToSign: boolean = false;
  protected needEconsent: boolean = false;
  protected needToValidateLoan: boolean = false;
  protected showDocumentList: boolean = false;

  protected zipCodePlaceHolder = 'XXXXX';
  protected zipCodeMask = '00000';
  protected zipCodePattern = '^[0-9]{5}?$';

  protected ssnPlaceHolder = 'XXXX';
  protected ssnMask = '0000';
  protected ssnPattern = '^[0-9]{4}?$';

  protected subjectPropertyZipCode: string;
  protected borrowerSsn: string;

  protected initialToken: string;

  protected signerViewUrl: SafeResourceUrl;
  protected documentSigningVendor: string;
  protected econsentGiven: boolean = false;

  protected isPropertyZipRequired?: boolean = false;
  protected isLast4DigitsOfSsnRequired?: boolean = false;

  protected esignOrderPackage: ValidateESignPropertyInfoResponse;

  protected portalSettings: PortalContent;

  private readonly _portalService: BasePortalService;

  protected _ordersToSign: EsignOrder[] = [];

  protected _finishedOrderIds: number[] = [];

  protected allDocsSigned: boolean = false;

  private _activeOrder: EsignOrder | null = null;
  private _activeSignerOrigin: string | null = null;
  private _messageHandlerRegistered: boolean = false;
  private readonly _popupCloseWarnThresholdMs: number = 10000;
  private readonly _popupClosePollIntervalMs: number = 250;
  private readonly _messageHandler = (event: MessageEvent) => {
    if (!event || !event.data) {
      return;
    }

    if (event.data.shouldCloseEsignDocIframe) {
      setTimeout(() => {
        const orderIdThatIsJustSigned = event.data.orderId;
        if (!this._finishedOrderIds.includes(orderIdThatIsJustSigned)) {
          this._finishedOrderIds.push(orderIdThatIsJustSigned);
          this.updateAllDocsSigned();
          const indexOfOrderJustSigned = this._ordersToSign.findIndex(o => o.signingOrder === orderIdThatIsJustSigned);
          const indexOfNextOrder = indexOfOrderJustSigned + 1;
          if (this.portalSettings?.allowRecipientsToSignInAnyOrder) {
            this.readyToSign = false;
            this.showDocumentList = true;
          } else if (indexOfNextOrder < this._ordersToSign.length) {
            this.gotoEsignView(this._ordersToSign[indexOfNextOrder]);
          }
        }
      }, 2000);
      return;
    }

    if (event.data.eSignVendorSigningEnded) {
      // This outcome handoff comes from our own confirmation route rendered inside the iframe, so it must
      // be same-origin; the cross-origin vendor signing page must not be able to drive the signing outcome.
      if (event.origin !== window.location.origin) {
        return;
      }
      this.reportVendorSigningOutcome(event.data.action, event.data.vendorQueryParameters);
      return;
    }

    if (event.data.authTokenRequested) {
      if (!this.isMessageFromSigningIframe(event)) {
        this.trackEvent('authTokenRequestedFromUnexpectedSource', { origin: event.origin });
        return;
      }

      if (!this._activeOrder) {
        this.trackEvent('authTokenRequestedWithoutActiveOrder', { origin: event.origin });
        return;
      }

      const isAllowlistedOrigin = this.isTokenRequestOriginAllowlisted(event.origin);
      if (!isAllowlistedOrigin) {
        // Monitor mode only: record the origin mismatch but preserve existing behavior.
        this.trackEvent('authTokenRequestedFromNonAllowlistedOrigin', {
          origin: event.origin,
          activeSignerOrigin: this._activeSignerOrigin,
        });
      }

      // Answered on a delay inherited from the Encompass POS flow this handshake came from. Note the pairing:
      // the requester - our confirmation page - gives up after TOKEN_RESPONSE_TIMEOUT_MS and tells the signer
      // the outcome went unrecorded, so this delay has to stay well inside that window.
      setTimeout(() => {
        const eventSource = event.source as Window | null;
        if (!eventSource) {
          this.trackEvent('authTokenRequestMissingEventSource', { origin: event.origin });
          return;
        }
        eventSource.postMessage(
          {
            authCompletionToken: this._activeOrder.jwtToken,
            // The signing session this page opened, for a requester that is our confirmation page on the
            // static vendor redirect domain and so reports the outcome itself. Withheld from an origin that
            // is not one of ours, which has no business reporting an outcome for this order.
            eSignCompletionContext: isAllowlistedOrigin ? this.buildCompletionContext() : undefined,
          },
          event.origin
        );
        this.trackEvent('authTokenResponsePosted', {
          origin: event.origin,
          isAllowlistedOrigin: isAllowlistedOrigin,
        });
      }, 1000);
    }
  };

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _portalServiceFactory: PortalServiceFactory,
    private readonly _domSanitizer: DomSanitizer,
    private readonly _authService: AuthService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _environmentService: EnvironmentService
  ) {
    if (this._activatedRoute.snapshot.url.find((s) => s.path.includes('agent-portal'))) {
      this.userScope = PortalScope.Agent;
    }
    this._portalService = this._portalServiceFactory.createByScope(
      this.userScope
    );

    this._authService.logout();
  }

  async ngOnInit() {
    this.registerMessageHandler();
    this.initialToken = await firstValueFrom(
      this._activatedRoute.queryParamMap.pipe(take(1))
    ).then((params) => params.get(Constants.token));
    if (this.initialToken) {
      await this.confirmEsignToken(this.initialToken);
    } else {
      this.alert = new Alert(
        'There is no token.',
        'There needs to be a token in the e-sign URL.',
        AlertType.Error
      );
    }
  }

  ngOnDestroy(): void {
    this.unregisterMessageHandler();
  }

  protected async onSubmitClicked() {
    if (!this.isFormValid()) {
      return;
    }
    this.alert = null;
    await this.validateLoan();
  }

  protected selectDocument(index: number) {
    this.showDocumentList = false;
    this.gotoEsignView(this._ordersToSign[index]);
  }

  protected async onEconsentSubmitClicked() {
    if (!this.isFormValid()) {
      return;
    }
    const observer: Observer<any> = {
      next: (result: any) => {
        this.isLoading = false;
        this.needEconsent = false;
        setTimeout(async () => {
          await this.startEsignProcess();
        });
      },
      error: (err: any) => {
        let errorMessage = 'An error occurred while submitting your e-consent. Please contact your loan officer for assistance.';
        if (err.status == 403) {
          errorMessage =
            'You do not have permission to submit e-consent for this loan.';
        } else {
          if (err.error?.detail) {
            errorMessage = err.error.detail;
          }
          // Here, we need to start over
          this.startOverTheProcess();
        }
        this.alert = new Alert(
          'A problem occurred.',
          errorMessage,
          AlertType.Error
        );
      },
      complete: () => { },
    };

    this.isLoading = true;
    this.loadingMessage = 'Submitting E-consent, please wait...';
    const orderThatHasPrivateTokenMatchingInitialOne =
      this._ordersToSign.find(o => o.publicToken.toLowerCase() === this.initialToken.toLowerCase());
    if (!orderThatHasPrivateTokenMatchingInitialOne) {
      this.isLoading = false;
      this.alert = new Alert(
        'A problem occurred.',
        'We could not locate the current signing order. Please restart the signing process.',
        AlertType.Error
      );
      return;
    }

    const jwtToken = orderThatHasPrivateTokenMatchingInitialOne.jwtToken;

    this._portalService
      .acceptEsignConsentForAnonymousEsign(
        this.initialToken,
        encodeURIComponent(orderThatHasPrivateTokenMatchingInitialOne.privateToken),
        this.esignOrderPackage.applicationId,
        this.esignOrderPackage.borrowerId,
        this.esignOrderPackage.mortgageBorrowerId,
        jwtToken
      )
      .subscribe(observer)
      .add(() => {
        this.isLoading = false;
        this._cdr.detectChanges();
      });
  }

  private startOverTheProcess = async () => {
    this.alert = null;
    this.signerViewUrl = null;
    this.needEconsent = false;
    this.needToValidateLoan = false;
    this.readyToSign = false;
    this.showDocumentList = false;
    this._ordersToSign = [];
    this._finishedOrderIds = [];
    this.updateAllDocsSigned();
    this._activeOrder = null;
    this._activeSignerOrigin = null;
    await this.confirmEsignToken(this.initialToken);
  };

  private getPortalSettings = async (jwtToken: string): Promise<void> => {
    this.isLoading = true;
    this.loadingMessage = 'Loading settings, please wait...';

    try {
      const response = await firstValueFrom(
        this._portalService.getPortalContentWithExternalToken(jwtToken)
      );
      this.portalSettings = response;
    this.updateAllDocsSigned();
    } catch (err) {
      this.alert = new Alert(
        'A problem occurred.',
        "An error occurred loading settings. Please contact your loan officer for assistance.",
        AlertType.Error
      );
    } finally {
      this.isLoading = false;
      this._cdr.detectChanges();
    }
  };

  private confirmEsignToken = async (token: string): Promise<void> => {
    this.isLoading = true;
    this.loadingMessage = 'Validating token, please wait...';

    try {
      const response = await firstValueFrom(
        this._portalService.confirmEsignToken(token)
      );
      this.needToValidateLoan =
        response.requiresSsnLastFourDigits ||
        response.requiresSubjectPropertyZip;
      this.isLast4DigitsOfSsnRequired = response.requiresSsnLastFourDigits;
      this.isPropertyZipRequired = response.requiresSubjectPropertyZip;
    } catch (err) {
      this.alert = new Alert(
        'A problem occurred.',
        this.getEsignErrorMessage(err),
        AlertType.Error
      );
    } finally {
      this.isLoading = false;
      this._cdr.detectChanges();
    }
  };

  // 403 means the signing token itself was rejected, 400 carries a borrower-safe
  // message from the api (eg a zip or ssn that doesn't match the loan).
  private getEsignErrorMessage = (err: any): string => {
    if (err?.status === 403) {
      return "An error occurred validating your token. It's either invalid or has expired.";
    }

    if (err?.status === 400 && err?.error?.detail) {
      return err.error.detail;
    }

    return 'An error has occurred.';
  };

  private updateAllDocsSigned() {
    this.allDocsSigned = !!(
      this._ordersToSign.length > 0 &&
      this._ordersToSign.every(order => this._finishedOrderIds.includes(order.signingOrder))
    );
  }

  private validateLoan = async () => {
    const observer: Observer<ValidateESignPropertyInfoResponse> = {
      next: async (response: any) => {
        this.isLoading = false;
        this.loadingMessage = '';
        this.needToValidateLoan = false;
        this.esignOrderPackage = response;
        const ordersToSign = this.esignOrderPackage.eSignOrderDetail.filter((o) => !!o.publicToken && !!o.privateToken);
        this._ordersToSign = _.orderBy(ordersToSign, ['signingOrder'], ['asc']);
        this.updateAllDocsSigned();
        this.needEconsent = !response.borrowerHasConsented;
        if (!this.needEconsent) {
          await this.startEsignProcess();
        } else if (response.eConsentText) {
          this.loadEconsentText(response.eConsentText);
        }
      },
      error: (err: any) => {
        this.alert = new Alert(
          'A problem occurred.',
          this.getEsignErrorMessage(err),
          AlertType.Error
        );
      },
      complete: () => { },
    };

    this.isLoading = true;
    this.loadingMessage = 'Validating loan, please wait...';
    const last4DigitsOfSsn = this.borrowerSsn?.slice(-4);
    this._portalService
      .validateLoanForAnonymousEsign(
        this.initialToken,
        this.subjectPropertyZipCode,
        last4DigitsOfSsn
      )
      .subscribe(observer)
      .add(() => {
        this.isLoading = false;
        this._cdr.detectChanges();
      });
  };

  private startEsignProcess = async () => {
    if (this._ordersToSign.length > 0) {
      await this.getPortalSettings(this._ordersToSign[0].jwtToken);

      if (this.portalSettings?.allowRecipientsToSignInAnyOrder &&
        this._ordersToSign.length > 1) {
        this.showDocumentList = true;
        this.readyToSign = false;
      } else {
        this.gotoEsignView(this._ordersToSign[0]);
      }
    }
  };

  private gotoEsignView = (order: EsignOrder) => {
    const observer: Observer<CreateSignerViewResponse> = {
      next: async (response: any) => {

        this._activeOrder = order;
        this._activeSignerOrigin = this.getOriginFromUrl(response.signerViewUrl);
        this.documentSigningVendor = response.documentSigningVendor;
        if (this.documentSigningVendor === 'EncompassPOS') {
          const eSignSSORedirectUrl = this._environmentService.eSignSSORedirectUrl;
          if (eSignSSORedirectUrl) {
            const cookieUrl = `${eSignSSORedirectUrl}?token=${encodeURIComponent(order.jwtToken)}`;
            const cookieWindow = window.open(cookieUrl, '_blank');
            if (cookieWindow) {
              await this.waitForPopupToClose(cookieWindow);
              this.trackEvent('ssoCookiePopupClosed');
            } else {
              this.trackEvent('ssoCookiePopupBlocked');
            }
          } else {
            this.trackEvent('missingESignSSORedirectUrl');
          }
        }
        this.signerViewUrl = this._domSanitizer.bypassSecurityTrustResourceUrl(
          response.signerViewUrl
        );
        this.readyToSign = true;
      },
      error: (err: any) => {
        let errorMessage = 'The data entered does not match our records, please try again.';
        if (err.status == 403) {
          errorMessage =
            'You do not have permission to submit e-consent for this loan.';
        } else {
          if (err.error?.detail) {
            errorMessage = err.error.detail;
          }
          // Here, we need to start over
          this.startOverTheProcess();
        }
        this.alert = new Alert(
          'A problem occurred.',
          errorMessage,
          AlertType.Error
        );
      },
      complete: () => { },
    };

    const portalUrlSegment =
      this.userScope == PortalScope.Borrower
        ? 'borrower-portal'
        : 'agent-portal';
    // Store JWT in localStorage by publicToken before redirect
    localStorage.setItem('esign_jwt_' + order.publicToken, order.jwtToken);
    const returnUrl = `${window.location.protocol}//${window.location.host}/${portalUrlSegment}/esign-confirmation/${order.publicToken}/${this._finishedOrderIds.length}/${this._ordersToSign.length}/${order.signingOrder}/${order.loanDocTaskId}`;
    const request = new OpenSignerViewRequest();
    request.returnUrl = returnUrl;
    request.loanDocTaskId = order.loanDocTaskId;
    request.applicationId = this.esignOrderPackage.applicationId;
    request.borrowerId = this.esignOrderPackage.borrowerId;

    this.isLoading = true;
    this.loadingMessage = 'Fetching esign URL, please wait...';
    this._portalService
      .openEsignerViewForAnonymousEsign(
        order.publicToken,
        encodeURIComponent(order.privateToken),
        order.loanDocTaskId,
        order.jwtToken,
        request
      )
      .subscribe(observer)
      .add(() => {
        this.isLoading = false;
        this._cdr.detectChanges();
      });
  };

  // The signing iframe cannot report the outcome to the api itself when the vendor redirect carries no order,
  // so this page reports it for the order it opened and then closes the iframe the way the other vendors do.
  private reportVendorSigningOutcome = (action: string, vendorQueryParameters: { key: string; value: string }[]) => {
    const order = this._activeOrder;
    if (!order) {
      this.trackEvent('vendorSigningEndedWithoutActiveOrder');
      return;
    }

    this._portalService
      .onSignerViewSubmittedForAnonymousEsign(
        order.publicToken,
        order.loanDocTaskId,
        action,
        order.jwtToken,
        vendorQueryParameters ?? []
      )
      .subscribe({
        next: (response) => {
          window.postMessage(
            {
              shouldCloseEsignDocIframe: true,
              orderId: order.signingOrder,
              response: response,
            },
            window.location.origin
          );
        },
        error: (error) => {
          console.error(error?.error?.message ?? 'E-sign confirmation failed.');
        },
      });
  };

  private isFormValid = (): boolean => {
    if (this.loanValidationForm) {
      this.loanValidationForm.form.markAllAsTouched();
      return !this.loanValidationForm.form.invalid;
    }
    return true;
  };

  private loadEconsentText = (econsentText: string) => {
    setTimeout(() => {
      const iframe = document.getElementById('econsent-text-iframe') as HTMLIFrameElement;
      if (!iframe) {
        return;
      }
      const doc = iframe.contentDocument;
      doc.open();
      doc.write(econsentText);
      doc.close();
    });
  }

  private waitForPopupToClose = (popup: Window): Promise<void> => {
    return new Promise<void>((resolve) => {
      let warningSent = false;
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          resolve();
          return;
        }
        if (!warningSent && Date.now() - startTime >= this._popupCloseWarnThresholdMs) {
          warningSent = true;
          this.trackEvent('ssoCookiePopupCloseTakingLongerThanExpected', {
            thresholdMs: this._popupCloseWarnThresholdMs
          });
        }
      }, this._popupClosePollIntervalMs);
    });
  };

  private registerMessageHandler = () => {
    if (this._messageHandlerRegistered) {
      return;
    }
    window.addEventListener('message', this._messageHandler, false);
    this._messageHandlerRegistered = true;
  };

  private unregisterMessageHandler = () => {
    if (!this._messageHandlerRegistered) {
      return;
    }
    window.removeEventListener('message', this._messageHandler, false);
    this._messageHandlerRegistered = false;
  };

  private getOriginFromUrl = (url: string | undefined): string | null => {
    if (!url) {
      return null;
    }
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  };

  private isTokenRequestOriginAllowlisted = (origin: string): boolean => {
    const allowlistedOrigins = new Set<string>();
    allowlistedOrigins.add(window.location.origin);
    if (this._activeSignerOrigin) {
      allowlistedOrigins.add(this._activeSignerOrigin);
    }

    const ssoRedirectOrigin = this.getOriginFromUrl(this._environmentService.eSignSSORedirectUrl);
    if (ssoRedirectOrigin) {
      allowlistedOrigins.add(ssoRedirectOrigin);
    }

    const vendorRedirectOrigin = this.getOriginFromUrl(this._environmentService.eSignVendorRedirectUrl);
    if (vendorRedirectOrigin) {
      allowlistedOrigins.add(vendorRedirectOrigin);
    }
    return allowlistedOrigins.has(origin);
  };

  private isMessageFromSigningIframe = (event: MessageEvent): boolean =>
    !!this._signingIframe && event.source === this._signingIframe.nativeElement.contentWindow;

  private buildCompletionContext = (): ESignCompletionContext => {
    const order = this._activeOrder;
    return {
      loanDocTaskId: order.loanDocTaskId,
      orderId: order.signingOrder,
      portalScope: this.userScope === PortalScope.Agent ? PortalScope.Agent : PortalScope.Borrower,
      publicToken: order.publicToken,
    };
  };

  private trackEvent = (eventName: string, payload: Record<string, unknown> = {}) => {
    if (!this._environmentService.production) {
      console.info('[ESignViaToken]', eventName, payload);
      return;
    }
    // In prod keep logs low-noise; still emit potential issues.
    if (eventName.includes('Timeout') || eventName.includes('Blocked') || eventName.includes('NonAllowlisted') || eventName.includes('Missing')) {
      console.warn('[ESignViaToken]', eventName, payload);
    }
  };
}
